import React from 'react';
import { Form, Select, Input, Radio } from 'antd';
import { FORM_FIELDS, INPUT_VALUE_MAX_LENGTH, RESULT_OPTIONS } from '../../constants';
import { FIELD_DISABLED_BY_SUPPLY_SOURCE } from '../constants';
import { CAS, DAS } from '../../AuctionSnapshotDetailedView/constants';
import { StyledFieldsWrapper, StyledMultipleSelect, StyledProfileSelect } from '../styles';

const { Option } = Select;

const groupCountriesByRegion = (selectedMarkets, snapshotCountriesParameters = {}) => {
  const markets = Array.isArray(selectedMarkets) ? selectedMarkets : selectedMarkets ? [selectedMarkets] : [];
  return markets.reduce((grouped, selectedMarket) => {
    const countries = snapshotCountriesParameters?.[selectedMarket] || [];
    if (countries.length) {
      grouped[selectedMarket] = [...new Set(countries)];
    }
    return grouped;
  }, {});
};

const getSelectedItemsPlaceholder = (selectedValues = []) => `... ${selectedValues.length} selected`;

export const UserParametersSection = (props) => {
  const form = Form.useFormInstance();
  const {
    profileTypes,
    handleChangeSelectType,
    isProfilesLoading,
    isSelectTypeValue,
    handleChangeProfile,
    handleProfilesFilter,
    handleClearUserProfile,
    profiles,
    handleChangeField,
    getOptionsByFieldName,
    supplySource,
    handleClearAppName,
    renderOptions,
    userProfile,
    publisher,
    podPosition,
    distributionPartner,
    market,
    snapshotCountriesParameters,
  } = props;
  const selectedMarkets = Form.useWatch(FORM_FIELDS.MARKET.name, form) || market || [];
  const selectedSovereignCountries = Form.useWatch(FORM_FIELDS.SOVEREIGN_COUNTRIES.name, form) || [];

  return (
    <StyledFieldsWrapper>
      <Form.Item label={FORM_FIELDS.SELECT_TYPE.label} name={FORM_FIELDS.SELECT_TYPE.name}>
        <Select onChange={handleChangeSelectType} placeholder="Select" options={profileTypes} allowClear />
      </Form.Item>
      <Form.Item
        label={FORM_FIELDS.USER_PROFILE.label}
        name={FORM_FIELDS.USER_PROFILE.name}
        loading={isProfilesLoading}
      >
        <StyledProfileSelect
          showSearch
          disabled={!isSelectTypeValue || isProfilesLoading}
          optionFilterProp="label"
          placeholder="Select"
          onChange={handleChangeProfile}
          filterOption={handleProfilesFilter}
          onClear={handleClearUserProfile}
          allowClear
          loading={isProfilesLoading}
        >
          {profiles?.currentUser && (
            <Select.OptGroup label="My Profiles">
              {profiles?.currentUser.map((profile) => (
                <Select.Option value={profile.value} key={profile.id}>
                  {profile.label}
                </Select.Option>
              ))}
            </Select.OptGroup>
          )}
          <Select.OptGroup label="All Profiles">
            {profiles?.allUsers?.map((profile) => (
              <Select.Option value={profile.value} key={profile.id}>
                {profile.label}
              </Select.Option>
            ))}
          </Select.OptGroup>
        </StyledProfileSelect>
      </Form.Item>
      <Form.Item
        label={FORM_FIELDS.APP_NAME.label}
        name={FORM_FIELDS.APP_NAME.name}
        rules={[{ required: true, message: 'Publisher is required!' }]}
      >
        <Select
          placeholder="Select"
          disabled={!supplySource}
          allowClear
          onClear={handleClearAppName}
          onChange={(value) => handleChangeField({ fieldName: FORM_FIELDS.APP_NAME.name, value })}
        >
          {getOptionsByFieldName(FORM_FIELDS.APP_NAME.name).map(renderOptions)}
        </Select>
      </Form.Item>
      <Form.Item label={FORM_FIELDS.PUBLISHER.label} name={FORM_FIELDS.PUBLISHER.name}>
        <Select
          placeholder="Select"
          disabled={!supplySource}
          allowClear
          onClear={handleClearAppName}
          onChange={(value) => handleChangeField({ fieldName: FORM_FIELDS.PUBLISHER.name, value })}
        >
          {getOptionsByFieldName(FORM_FIELDS.PUBLISHER.name).map(renderOptions)}
        </Select>
      </Form.Item>
      <Form.Item label={FORM_FIELDS.DISTRIBUTION_PARTNER.label} name={FORM_FIELDS.DISTRIBUTION_PARTNER.name}>
        <Select placeholder="Select" allowClear>
          {distributionPartner?.map(renderOptions)}
        </Select>
      </Form.Item>
      <Form.Item label={FORM_FIELDS.DISTRIBUTOR.label} name={FORM_FIELDS.DISTRIBUTOR.name}>
        <Select
          placeholder="Select"
          allowClear
          onChange={(value) => handleChangeField({ fieldName: FORM_FIELDS.DISTRIBUTOR.name, value })}
        >
          {getOptionsByFieldName(FORM_FIELDS.DISTRIBUTOR.name).map(renderOptions)}
        </Select>
      </Form.Item>
      <Form.Item
        label={FORM_FIELDS.MARKET.label}
        name={FORM_FIELDS.MARKET.name}
        rules={[{ required: supplySource === CAS || supplySource === DAS, message: 'Market is required!' }]}
      >
        <StyledMultipleSelect
          placeholder="Select"
          disabled={!supplySource || !publisher}
          allowClear
          mode="multiple"
          maxTagCount="responsive"
          maxTagPlaceholder={() => getSelectedItemsPlaceholder(selectedMarkets)}
        >
          {Object.keys(snapshotCountriesParameters).map(renderOptions)}
        </StyledMultipleSelect>
      </Form.Item>
      <Form.Item label={FORM_FIELDS.SOVEREIGN_COUNTRIES.label} name={FORM_FIELDS.SOVEREIGN_COUNTRIES.name}>
        <StyledMultipleSelect
          placeholder="Select"
          disabled={!supplySource || !publisher || !selectedMarkets?.length}
          allowClear
          mode="multiple"
          maxTagCount="responsive"
          maxTagPlaceholder={() => getSelectedItemsPlaceholder(selectedSovereignCountries)}
        >
          {Object.entries(groupCountriesByRegion(selectedMarkets, snapshotCountriesParameters)).map(
            ([region, countries]) => (
              <Select.OptGroup label={region} key={region}>
                {countries.map((country) => (
                  <Option value={country} key={country}>
                    {country}
                  </Option>
                ))}
              </Select.OptGroup>
            )
          )}
        </StyledMultipleSelect>
      </Form.Item>
      <Form.Item label={FORM_FIELDS.POD_POSITION.label} name={FORM_FIELDS.POD_POSITION.name}>
        <Select placeholder="Select" allowClear>
          {podPosition?.map(renderOptions)}
        </Select>
      </Form.Item>
      <Form.Item
        label={FORM_FIELDS.INPUT_VALUE.label}
        name={FORM_FIELDS.INPUT_VALUE.name}
        info="Info Text"
        rules={[
          {
            required: !!userProfile,
            message: `${FORM_FIELDS.INPUT_VALUE.label} is required!`,
          },
        ]}
      >
        <Input disabled={userProfile || !isSelectTypeValue} maxLength={INPUT_VALUE_MAX_LENGTH} showCount allowClear />
      </Form.Item>
      <Form.Item label={FORM_FIELDS.SESSION_ID.label} name={FORM_FIELDS.SESSION_ID.name}>
        <Input
          disabled={!supplySource || FIELD_DISABLED_BY_SUPPLY_SOURCE?.[FORM_FIELDS?.SESSION_ID?.name]?.[supplySource]}
          allowClear
        />
      </Form.Item>
      <Form.Item label={FORM_FIELDS.EXPERIMENT_ID.label} name={FORM_FIELDS.EXPERIMENT_ID.name}>
        <Input
          disabled={
            !supplySource || FIELD_DISABLED_BY_SUPPLY_SOURCE?.[FORM_FIELDS?.EXPERIMENT_ID?.name]?.[supplySource]
          }
          allowClear
        />
      </Form.Item>
      <Form.Item label={FORM_FIELDS.RESULT.label} name={FORM_FIELDS.RESULT.name}>
        <Radio.Group options={RESULT_OPTIONS} />
      </Form.Item>
    </StyledFieldsWrapper>
  );
};
